import React from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';

const serviceData = {
  'flight-booking': {
    title: 'Flight Booking',
    icon: '✈️',
    inquiryType: 'flight',
    image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80',
    imageAlt: 'Airplane wing above clouds at golden hour',
    intro: 'Budget-friendly fares on domestic and international routes, with fare-watch alerts and flexible date support.',
    features: ['Domestic & international tickets', 'Multi-city and round-trip routing', 'Fare comparison across airlines', 'Date-change and cancellation support']
  },
  'visa-assistance': {
    title: 'Visa Assistance',
    icon: '🛂',
    inquiryType: 'visa',
    image: 'https://i.pinimg.com/1200x/42/86/a4/4286a481f1cf676faef7559c98ffbe74.jpg',
    imageAlt: 'Passport and travel documents on a desk',
    intro: 'End-to-end documentation support for tourist, business, and transit visas to popular destinations worldwide.',
    features: ['Document checklist preparation', 'Application form assistance', 'Appointment scheduling', 'Visa status tracking updates']
  },
  'hotel-booking': {
    title: 'Hotel Booking',
    icon: '🏨',
    inquiryType: 'hotel',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
    imageAlt: 'Luxury hotel pool at sunset',
    intro: 'From boutique stays to five-star luxury, we match you with hotels that fit your itinerary and budget.',
    features: ['Curated luxury & budget options', 'Group and family bookings', 'Best-rate negotiation', 'Special requests handled directly']
  },
  'travel-insurance': {
    title: 'Travel Insurance',
    icon: '🛡️',
    inquiryType: 'general',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80',
    imageAlt: 'Person signing insurance documents at a desk',
    intro: 'Comprehensive coverage for medical emergencies, trip cancellations, and lost baggage — so you can travel worry-free.',
    features: ['Medical emergency coverage', 'Trip cancellation protection', 'Baggage loss & delay cover', 'Single-trip & annual plans']
  },
  'airport-transfer': {
    title: 'Airport Transfer',
    icon: '🚗',
    inquiryType: 'general',
    image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&q=80',
    imageAlt: 'Luxury car driving on a highway at night',
    intro: 'Reliable, on-time pickups and drop-offs so your journey starts and ends smoothly.',
    features: ['Private and shared transfers', 'Meet-and-greet service', 'Fixed, transparent pricing', '24/7 availability']
  },
  'cruise-packages': {
    title: 'Cruise Packages',
    icon: '🚢',
    inquiryType: 'holiday',
    image: 'https://images.unsplash.com/photo-1548574505-5e239809ee19?w=800&q=80',
    imageAlt: 'Large cruise ship on open ocean at sunrise',
    intro: 'Curated cruise itineraries across the Mediterranean, Caribbean, and Asia-Pacific routes.',
    features: ['Cabin selection guidance', 'Shore excursion planning', 'Group cruise bookings', 'Onboard upgrade assistance']
  },
  'holiday-packages': {
    title: 'Holiday Packages',
    icon: '🧳',
    inquiryType: 'holiday',
    image: 'https://i.pinimg.com/736x/7b/9a/e3/7b9ae3d72da302c24ad58d8e397777bf.jpg',
    imageAlt: 'Tropical beach with clear turquoise water',
    intro: 'Custom holiday itineraries — honeymoons, family trips, or solo adventures — built entirely around you.',
    features: ['Fully customisable itineraries', 'Flights + hotels + transfers bundled', 'Local guide arrangements', 'Family and group discounts']
  },
  'corporate-travel-services': {
    title: 'Corporate Travel Services',
    icon: '💼',
    inquiryType: 'general',
    image: 'https://i.pinimg.com/736x/fb/1b/b8/fb1bb898f3457e834ab510c6e0113a45.jpg',
    imageAlt: 'Modern corporate office meeting room',
    intro: 'Dependable travel management for businesses — from single trips to company-wide travel policies.',
    features: ['Dedicated account manager', 'Centralised billing', 'Policy-compliant booking', 'Priority support for changes']
  }
};

function ServiceImageCard(props) {
  var service = props.service;
  var navigate = useNavigate();
  var [hovered, setHovered] = React.useState(false);
  var [btnHovered, setBtnHovered] = React.useState(false);

  function handleClick() {
    navigate('/contact?type=' + service.inquiryType);
  }

  return (
    <div
      onMouseEnter={function() { setHovered(true); }}
      onMouseLeave={function() { setHovered(false); }}
      style={{
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: hovered
          ? '0 24px 56px rgba(17,45,78,0.18)'
          : '0 6px 24px rgba(17,45,78,0.09)',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        background: '#fff',
        maxWidth: 400
      }}
    >
      <div style={{ position: 'relative', overflow: 'hidden', height: 220 }}>
        <img
          src={service.image}
          alt={service.imageAlt}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: hovered ? 'scale(1.06)' : 'scale(1)',
            transition: 'transform 0.5s ease'
          }}
        />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, rgba(17,45,78,0.55) 0%, transparent 60%)'
        }} />
        <div style={{
          position: 'absolute', bottom: 14, left: 16,
          display: 'flex', alignItems: 'center', gap: 8
        }}>
          <span style={{
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(6px)',
            borderRadius: 8,
            padding: '4px 10px',
            fontSize: '1.1rem'
          }}>
            {service.icon}
          </span>
          <span style={{
            color: '#fff', fontWeight: 700, fontSize: '1rem',
            textShadow: '0 1px 4px rgba(0,0,0,0.3)'
          }}>
            {service.title}
          </span>
        </div>
      </div>

      <div style={{ padding: '20px 22px 24px' }}>
        <p style={{
          margin: '0 0 20px', fontSize: '0.9rem',
          color: 'var(--bj-espresso)', opacity: 0.75, lineHeight: 1.65
        }}>
          Ready to book? Send us your details and our {service.title.toLowerCase()} specialist will get back to you promptly.
        </p>

        <button
          onClick={handleClick}
          onMouseEnter={function() { setBtnHovered(true); }}
          onMouseLeave={function() { setBtnHovered(false); }}
          style={{
            width: '100%',
            padding: '13px 0',
            borderRadius: 8,
            border: '2px solid var(--bj-clay)',
            background: btnHovered ? 'var(--bj-clay)' : 'transparent',
            color: btnHovered ? '#fff' : 'var(--bj-clay)',
            fontWeight: 700,
            fontSize: '0.95rem',
            cursor: 'pointer',
            transition: 'background 0.2s ease, color 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            fontFamily: 'var(--font-body)'
          }}
        >
          Get a Quote
          <span style={{
            display: 'inline-block',
            transform: btnHovered ? 'translateX(4px)' : 'translateX(0)',
            transition: 'transform 0.2s ease'
          }}>→</span>
        </button>
      </div>
    </div>
  );
}

export default function ServicePage() {
  var params = useParams();
  var service = serviceData[params.slug];

  if (!service) return <Navigate to="/" replace />;

  return (
    <Layout>
      <section className="section">
        <div className="container service-page-grid" style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 40, alignItems: 'start' }}>
          <div>
            <span className="eyebrow">{service.icon} Service</span>
            <h1>{service.title}</h1>
            <p style={{ fontSize: '1.05rem', lineHeight: 1.7, maxWidth: 560 }}>{service.intro}</p>
            <div className="flight-path" style={{ margin: '28px 0' }} />
            <h3 style={{ marginBottom: 14 }}>What is included</h3>
            <ul style={{ paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {service.features.map(function(f) {
                return (
                  <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.95rem' }}>
                    <span style={{
                      width: 22, height: 22, borderRadius: '50%',
                      background: 'var(--bj-sand)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.7rem', color: 'var(--bj-clay)', fontWeight: 700,
                      flexShrink: 0
                    }}>✓</span>
                    {f}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="service-image-card" style={{ position: 'sticky', top: 100 }}>
            <ServiceImageCard service={service} />
          </div>
        </div>
      </section>
    </Layout>
  );
}